
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import InterviewLoading from "@/components/candidate/interviews/InterviewLoading.vue";
import AppAssessmentTermConditions from "@/components/AppAssessmentTermConditions.vue";
import AppAiAssessmentRoot from "@/components/candidate/app_assessments/ai_assessment/AppAiAssessmentRoot.vue";
import { ApplicantAssessment } from "@/store/modules/recruiter/interfaces";
import { GET_USER_DETAILS } from "@/store/modules/auth/constants";
import { APPLICANT_ASSESSMENTS } from "@/store/modules/recruiter/constants";
import { FETCH_APPLICANT_ASSESSMENTS } from "@/store/modules/recruiter/constants";
import {
  redo_assessment_request,
  request_assessment_resume,
  cancel_my_assessment_attempt
} from "@/store/apis/recruiter";
import domtoimage from "dom-to-image-more";
import { get_random_number_between, wait_until } from "@/utils/global";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { APP_ASSESSMENT_RESULT } from "@/store/modules/common/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import store from "@/store";
import i18n from "@/i18n";
import router from "@/router";

export default Vue.extend({
  name: "InceptionAiAssessmentIndex",
  components: {
    InterviewLoading,
    AppAssessmentTermConditions,
    AppAiAssessmentRoot
  },
  data() {
    return {
      term_condition_accepted: false, // Set to true if terms already accepted
      data_loading: false,
      local_assessment_completed: false,
      data_error: "",
      assessment_cancelled: false,
      assessment_completed: false,
      assessment_recording_started: false, // True only after all permissions granted + mark_assessment_resumed called
      loading: false,
      assessments: [] as ApplicantAssessment[],
      screen_shots: [] as string[],
      startingAssessmentId: null as number | null,
      leave_dialog_visible: false,
      leave_dialog_resolve: null as ((confirmed: boolean) => void) | null,
      leave_dialog_confirmed: false,
      beforeUnloadHandler: null as ((event: BeforeUnloadEvent) => void) | null
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter", {
      applicantAssessments: APPLICANT_ASSESSMENTS
    })
  },
  async created() {
    await this.init();
  },
  async mounted() {
    await this.init();

    // Set up beforeunload handler for page reload detection
    // This ensures that if user reloads the page during an active assessment,
    // we show the Leave Assessment dialog
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      const assessmentIsActive =
        this.$route.params.id &&
        this.term_condition_accepted &&
        this.assessment_recording_started &&
        !this.assessment_completed &&
        !this.assessment_cancelled;

      if (assessmentIsActive) {
        try {
          // Fire-and-forget – backend will mark this attempt as
          // "Admin Approval Required" for any non-submitted exit.
          request_assessment_resume(Number(this.$route.params.id));
        } catch (_e) {
          // ignore – best-effort only
        }
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  },
  methods: {
    ...mapActions("recruiter", {
      fetchApplicantAssessments: FETCH_APPLICANT_ASSESSMENTS
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async init(this: any) {
      if (this.term_condition_accepted) {
        // We're viewing the assessment list
        await this.loadAssessments();
        return;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async loadAssessments(this: any) {
      this.loading = true;
      try {
        const applicantId = this.user?.id;
        if (!applicantId) {
          throw new Error("User ID not found");
        }

        const success = await this.fetchApplicantAssessments(
          applicantId.toString()
        );
        if (success) {
          const raw = this.applicantAssessments;
          this.assessments = Array.isArray(raw) ? raw : raw?.data ?? [];
        }
      } catch (error) {
        console.error("Failed to load assessments:", error);
        this.data_error = this.$t("assessment.load-error").toString();
        this.$store.commit("ROOT_ERROR", error, { root: true });
      } finally {
        this.loading = false;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async requestResumeOnLeave(this: any) {
      const id = Number(this.$route.params.id);
      if (
        !id ||
        this.assessment_completed ||
        this.assessment_cancelled ||
        this.local_assessment_completed ||
        !this.term_condition_accepted
      ) {
        return;
      }
      // If the assessment never truly started (screen share was cancelled before
      // mark_assessment_resumed was called), cancel the in-progress attempt so
      // the list shows "Start Assessment" instead of "Need Admin Approval".
      if (!this.assessment_recording_started) {
        try {
          await cancel_my_assessment_attempt(id);
        } catch (_e) {
          // non-blocking
        }
        return;
      }
      try {
        await request_assessment_resume(id);
      } catch (_e) {
        // non-blocking
      }
    },
    async process_term_condition(val: boolean) {
      if (!val) {
        console.log("Before push");
        this.local_assessment_completed = true;
        // If user is on a specific assessment route (:id) and declines terms,
        // cancel that in-progress attempt so it shows as "Start Assessment" next time.
        const idParam = this.$route.params.id;
        const assessmentId = Number(idParam);
        if (assessmentId) {
          try {
            await cancel_my_assessment_attempt(assessmentId);
          } catch (_e) {
            // Non-blocking: still navigate so user sees list/dashboard
          }
        }
        await this.$router.push("/candidate/ai-assessments");
      } else {
        this.term_condition_accepted = true;
        console.log("Before init");
        await this.init();
        // Screenshot capture
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (
            this.assessment_cancelled ||
            this.assessment_completed ||
            this.local_assessment_completed ||
            !this.term_condition_accepted
          )
            break;
          const randomNumber = get_random_number_between(8, 12);
          await wait_until(randomNumber * 1000);
          const img = await domtoimage.toPng(document.body);
          this.screen_shots.push(img);
        }
      }
    },
    getDifficultyColor(difficulty: string): string {
      switch (difficulty.toLowerCase()) {
        case "beginner":
        case "easy":
          return "success";
        case "intermediate":
        case "medium":
          return "warning";
        case "advanced":
        case "hard":
          return "error";
        default:
          return "grey";
      }
    },
    formatDate(dateString: string): string {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    async requestCameraAndMicThenNavigate(assessment: ApplicantAssessment) {
      this.startingAssessmentId = assessment.assessment.id;
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.browser-unsupport-camera").toString(),
            { root: true }
          );
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        if (stream && stream.getTracks().length > 0) {
          stream.getTracks().forEach((t) => t.stop());
          this.$router.push({
            name: "candidate-ai-assessment",
            params: { id: assessment.assessment.id.toString() }
          });
        }
      } catch (e) {
        const err = e as { name?: string };
        if (
          err?.name === "NotAllowedError" ||
          err?.name === "PermissionDeniedError"
        ) {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.permission-denied").toString(),
            { root: true }
          );
        } else if (
          err?.name === "NotFoundError" ||
          err?.name === "DevicesNotFoundError"
        ) {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.no-camera-device").toString(),
            { root: true }
          );
        } else {
          this.$store.commit(
            "ROOT_ERROR",
            this.$t("assessments.permission-denied").toString(),
            { root: true }
          );
        }
      } finally {
        this.startingAssessmentId = null;
      }
    },
    onCardClick(assessment: ApplicantAssessment) {
      if (this.isCompleted(assessment) || this.isRedo(assessment)) return;
      this.handlePrimaryAction(assessment);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handlePrimaryAction(this: any, assessment: ApplicantAssessment) {
      if (
        this.isRedo(assessment) ||
        this.isCompleted(assessment) ||
        this.isAdminApprovalRequired(assessment)
      )
        return;
      if (this.isCancelled(assessment)) {
        this.requestRedo(assessment);
        return;
      }
      this.requestCameraAndMicThenNavigate(assessment);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isCompleted(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "COMPLETED" || status === "RESULT_PROCESSING";
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isCancelled(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "CANCELLED";
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isRedo(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "REDO";
    },
    /** True when assessment is in progress and admin has approved resume, so candidate should continue where they left off. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    canResume(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      if (status !== "IN_PROGRESS") return false;
      const adminStatus =
        assessment?.resume_admin_status ??
        assessment?.result?.resume_admin_status ??
        null;
      return adminStatus === "APPROVED";
    },
    /** True when assessment is in progress and user needs admin approval to resume (left in between) */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isAdminApprovalRequired(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      if (status !== "IN_PROGRESS") return false;
      const adminStatus =
        assessment?.resume_admin_status ??
        assessment?.result?.resume_admin_status ??
        null;
      // PENDING or missing → need admin approval; only APPROVED can resume without showing this
      return adminStatus !== "APPROVED";
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extractStatus(assessment: any): string | undefined {
      const directStatus = assessment?.status;
      const resultStatus = assessment?.result?.status;
      const firstResultStatus = Array.isArray(assessment?.results)
        ? assessment.results[0]?.status
        : undefined;
      return directStatus || resultStatus || firstResultStatus;
    },
    async requestRedo(assessment: ApplicantAssessment) {
      const response = await redo_assessment_request(assessment.assessment.id);
      if (response) {
        await this.loadAssessments();
        this.$store.commit(
          "ROOT_NOTIFICATION",
          this.$t("assessments.redo_notification").toString(),
          { root: true }
        );
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    goToDashboard(this: any) {
      this.$router.push("/candidate/dashboard");
    },
    showLeaveConfirmation(): Promise<boolean> {
      return new Promise((resolve) => {
        this.leave_dialog_resolve = resolve;
        this.leave_dialog_visible = true;
      });
    },
    onLeaveDialogStay() {
      this.leave_dialog_visible = false;
      if (this.leave_dialog_resolve) {
        this.leave_dialog_resolve(false);
        this.leave_dialog_resolve = null;
      }
    },
    onLeaveDialogLeave() {
      this.leave_dialog_visible = false;
      if (this.leave_dialog_resolve) {
        this.leave_dialog_resolve(true);
        this.leave_dialog_resolve = null;
      }
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async beforeRouteLeave(this: any, to, from, next) {
    const win = window as unknown as { __hcmsLoggingOut?: boolean };
    const isLoggingOut = win.__hcmsLoggingOut;
    // Only guard when an AI assessment is actively in progress.
    const assessmentIsActive =
      this.$route.params.id &&
      this.term_condition_accepted &&
      this.assessment_recording_started &&
      !this.assessment_completed &&
      !this.assessment_cancelled;

    if (assessmentIsActive && !isLoggingOut && !this.leave_dialog_confirmed) {
      const confirmed = await this.showLeaveConfirmation();
      if (!confirmed) {
        next(false);
        return;
      }
      this.leave_dialog_confirmed = true;
    }

    if (this.$route.params.id) {
      await this.requestResumeOnLeave();
    }
    this.local_assessment_completed = true;
    next();
  },
  async beforeRouteEnter(to, from, next) {
    const idParam = to.params.id;
    if (!idParam) {
      // List view (/assessments/ai) – no per-assessment resume gating needed
      return next();
    }

    const assessmentId = Number(idParam);
    if (!assessmentId) {
      return next();
    }

    // Fetch latest assessment_result for this assessment + user
    const assessment_exist = await store.dispatch(
      `common/${APP_ASSESSMENT_RESULT}`,
      {
        assessment_id: assessmentId
      }
    );

    const status =
      assessment_exist?.status || assessment_exist?.result?.status || null;
    const overallScore =
      assessment_exist?.overall_score ??
      assessment_exist?.result?.overall_score ??
      null;
    const hasScore = overallScore !== null && overallScore !== undefined;

    const isInProgress =
      status === AppAssessments.AppAssessmentStatus.IN_PROGRESS;
    const stuckNoScore =
      status === AppAssessments.AppAssessmentStatus.RESULT_PROCESSING &&
      !hasScore;

    // Terminal states (completed / failed / cancelled etc.) → don't allow entering again here
    if (assessment_exist && !isInProgress && !stuckNoScore) {
      // prettier-ignore
      store.commit(ROOT_NOTIFICATION, i18n.t("assessments.completed-already"));
      if (router.currentRoute.name !== "candidate-dashboard") {
        await router.push("/candidate/dashboard");
      }
      return next(false);
    }

    if (assessment_exist && isInProgress) {
      const adminStatus = assessment_exist.resume_admin_status ?? null;

      if (adminStatus === "APPROVED") {
        // Admin approved resume – allow entry.
        // The assessment will be marked as resumed only after
        // the candidate accepts terms and shares camera + screen
        // inside the assessment component.
      } else {
        // No approval yet: request resume and block entry; stay on Additional Assessments list
        try {
          await request_assessment_resume(assessmentId);
        } catch (_e) {
          // ignore – still block direct resume
        }
        store.commit(
          ROOT_NOTIFICATION,
          i18n.t("assessments.resume-awaiting-approval")
        );
        if (router.currentRoute.name !== "candidate-ai-assessments") {
          await router.push("/candidate/ai-assessments");
        }
        return next(false);
      }
    }

    return next();
  },
  beforeDestroy() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
});
